import type { AdminRepository } from "./admin-repository";
import type { AdminPermission } from "./session-api";

// Explicit local synthetic API only. No reflection, snapshot, auth, file or arbitrary command route.
export const demoWorkspaceReads = {
  getDashboardSummary: "admin.access",
  listScheduleCalendar: "partner-schedules.read",
  listScheduleSeries: "partner-schedules.read",
  getScheduleSeries: "partner-schedules.read",
  previewScheduleSeries: "partner-schedules.read",
  previewScheduleSeriesUpdate: "partner-schedules.read",
  listPartnerSetups: "partner.import",
  getPartnerSetup: "partner.import",
  listUsers: "users.read",
  getUser: "users.read",
  listUserSessions: "sessions.read",
  listUserLegalAcceptances: "legal.acceptance.read",
  listAdministrators: "admins.manage",
  listInvitations: "users.invite",
  getInvitation: "users.invite",
  listRoles: "roles.manage",
  listEffectiveActionPolicies: "approvals.read",
  listActionPolicies: "approval-policies.manage",
  listExecutionControls: "approval.execution.manage",
  listSensitiveReadControls: "approval.execution.manage",
  listApprovalPolicies: "approvals.read",
  listApprovalRequests: "approvals.read",
  getApprovalRequest: "approvals.read",
  listNotifications: "notifications.read",
  listCanonicalCities: "destinations.read",
  getCanonicalCity: "destinations.read",
  listDestinationReferences: "destinations.read",
  getDestinationReference: "destinations.read",
  listSystemExperiences: "experiences.read",
  listExperienceOfferings: "experiences.read",
  listScheduleOverview: "partner-schedules.read",
  getSystemExperience: "experiences.read",
  listPartners: "partners.read",
  listPartnerTeam: "partner-team.manage",
  getPartner: "partners.read",
  listPartnerHistory: "partners.read",
  listPartnerContacts: "partner-contacts.read",
  getPartnerContact: "partner-contacts.read",
  previewPartnerContactErasure: "partner-contacts.erase",
  getPartnerContactErasure: "partner-contacts.erase",
  getPartnerContactEvidenceHold: "partner.evidence.hold.manage",
  listPartnerOfferings: "partner-offerings.read",
  getPartnerOffering: "partner-offerings.read",
  listPartnerSchedules: "partner-schedules.read",
  getPartnerSchedule: "partner-schedules.read",
  previewPartnerSchedule: "partner-schedules.read",
  listDestinations: "destinations.read",
  listExperiences: "experiences.read",
  listLegalDocuments: "legal.manage",
  getLegalDocument: "legal.manage",
  listAuditTrail: "audit.read",
  getAuditTrailDetail: "audit.read",
  listPartnerImportBatches: "partner.import",
  getPartnerImportBatch: "partner.import",
  getPartnerImportDefinition: "partner.import",
  getPartnerImportSummary: "partner.import",
  listPartnerImportReview: "partner.import",
  locatePartnerImportRow: "partner.import",
  exportPartnerImport: "partner.import",
} as const satisfies Partial<Record<keyof AdminRepository, AdminPermission>>;
export const demoWorkspaceWrites = {
  createScheduleSeries: "partner-schedules.manage",
  updateScheduleSeries: "partner-schedules.manage",
  createPartnerSetup: "partner.import",
  savePartnerSetup: "partner.import",
  mutateAccess: "users.access.manage",
  mutateAdministrator: "admins.manage",
  revokeSessions: "sessions.revoke",
  updateRoles: "roles.manage",
  resetAdministratorMfa: "admins.manage",
  inviteUser: "users.invite",
  invitePartnerContentManager: "partner-team.manage",
  changePartnerTeamMembership: "partner-team.manage",
  resendInvitation: "users.invite",
  cancelInvitation: "users.invite",
  updateActionPolicy: "approval-policies.manage",
  updateExecutionControl: "approval.execution.manage",
  updateSensitiveReadControl: "approval.execution.manage",
  createApprovalRequest: "approvals.read",
  decideApprovalRequest: "approvals.decide",
  applyApprovalRequest: "approvals.decide",
  retryApprovalRequest: "approvals.decide",
  releaseApprovalExecution: "approval.execution.manage",
  withdrawApprovalRequest: "approvals.read",
  reviseApprovalRequest: "approvals.read",
  markNotificationRead: "notifications.read",
  createCanonicalCity: "destinations.manage",
  updateCanonicalCity: "destinations.manage",
  createDestinationReference: "destinations.manage",
  updateDestinationReference: "destinations.manage",
  updateDestinationLifecycle: "destinations.manage",
  createPartner: "partners.manage",
  updatePartner: "partners.manage",
  updatePartnerLifecycle: "partners.manage",
  createPartnerContact: "partner-contacts.manage",
  updatePartnerContact: "partner-contacts.manage",
  updatePartnerContactLifecycle: "partner-contacts.manage",
  erasePartnerContact: "partner-contacts.erase",
  changePartnerContactEvidenceHold: "partner.evidence.hold.manage",
  createExperience: "experiences.manage",
  updateExperience: "experiences.manage",
  updateExperienceLifecycle: "experiences.manage",
  createPartnerOffering: "partner-offerings.manage",
  updatePartnerOffering: "partner-offerings.manage",
  updatePartnerOfferingLifecycle: "partner-offerings.manage",
  createPartnerSchedule: "partner-schedules.manage",
  updatePartnerSchedule: "partner-schedules.manage",
  updatePartnerScheduleStatus: "partner-schedules.manage",
  saveLegalDraft: "legal.manage",
  createLegalRevision: "legal.manage",
  publishLegal: "legal.manage",
  createPartnerImportPreview: "partner.import",
  stagePartnerImport: "partner.import",
  regeneratePartnerImport: "partner.import",
  updatePartnerImportDecision: "partner.import",
  applyPartnerImport: "partner.import",
  controlPartnerImport: "partner.import",
} as const satisfies Partial<Record<keyof AdminRepository, AdminPermission>>;
export const demoWorkspaceMethods = { ...demoWorkspaceReads, ...demoWorkspaceWrites };
export type DemoWorkspaceMethod = keyof typeof demoWorkspaceMethods;
export type DemoWorkspaceArgs<K extends DemoWorkspaceMethod> = Parameters<AdminRepository[K]>;
export type DemoWorkspaceResult<K extends DemoWorkspaceMethod> = Awaited<
  ReturnType<AdminRepository[K]>
>;
export const demoWorkspaceProtocol = "synthetic-workspace-v1";
export function isDemoWorkspaceMethod(value: string): value is DemoWorkspaceMethod {
  return Object.hasOwn(demoWorkspaceMethods, value);
}
